import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Table, Button, Row, Col, Spinner } from 'reactstrap';
import { connect } from 'react-redux';
import { setBreadcrumbItems } from '../store/actions';
import api from '../utils/api';
import { Link } from 'react-router-dom';

const UsersList = (props) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    props.setBreadcrumbItems('المستخدمين', [
      { title: 'الرئيسية', link: '/dashboard' },
      { title: 'المستخدمين', link: '#' }
    ]);
  }, [props]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) setCurrentUser(JSON.parse(raw));
    } catch {
      setCurrentUser(null);
    } finally {
      setCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser && isAdmin()) {
      fetchUsers();
    } else if (!currentUser) {
      setLoadingUsers(false);
    }
  }, [currentUser]);

  const isAdmin = () => currentUser?.role?.toLowerCase() === 'admin';

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/users');
      const list = res.data?.users || res.data || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      await api.post(`/users/${id}/activate`, { is_active: !isActive });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: !isActive } : u));
    } catch {
      alert('فشل تغيير الحالة');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    try {
      await api.delete(`/users/${id}`);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch {
      alert('فشل الحذف');
    }
  };

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Row className="mb-4">
            <Col md={6}>
              <CardTitle className="h4 mb-0">قائمة المستخدمين</CardTitle>
            </Col>
            <Col md={6} className="text-end">
              {isAdmin() && (
                <Link to="/dashboard/users/create">
                  <Button color="success" className="btn-rounded">
                    <i className="fas fa-plus me-1" /> إضافة مستخدم جديد
                  </Button>
                </Link>
              )}
            </Col>
          </Row>

          {checkingAuth ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 mb-0">جاري التحقق من الصلاحيات...</p>
            </div>
          ) : !isAdmin() ? (
            <div className="text-center py-5">
              <i className="fas fa-lock fa-3x mb-3 text-muted"></i>
              <h5>غير مسموح</h5>
              <p className="text-muted mb-0">ليس لديك صلاحية لعرض هذه الصفحة</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table align-middle table-bordered table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الاسم</th>
                    <th>البريد الإلكتروني</th>
                    <th>الصلاحية</th>
                    <th>الحالة</th>
                    <th>تاريخ الإنشاء</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingUsers ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <Spinner size="sm" color="primary" />
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">لا يوجد مستخدمون</td>
                    </tr>
                  ) : users.map((user, index) => {
                    const created = user.created_at ? new Date(user.created_at) : null;
                    const dateStr = created && !isNaN(created) ? created.toLocaleDateString('ar-EG') : '-';
                    return (
                      <tr key={user.id || index}>
                        <td>{index + 1}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`badge ${
                            user.role?.toLowerCase() === 'admin'
                              ? 'bg-danger'
                              : user.role === 'data_entry'
                                ? 'bg-warning'
                                : 'bg-info'
                          }`}>
                            {user.role === 'Admin' ? 'مدير' :
                              user.role === 'data_entry' ? 'مدخل بيانات' : 'مستخدم'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                            {user.is_active ? 'مفعل' : 'معطل'}
                          </span>
                        </td>
                        <td>{dateStr}</td>
                        <td>
                          {user.id !== 1 && (
                            <Button
                              size="sm"
                              color={user.is_active ? 'warning' : 'success'}
                              onClick={() => handleToggleActive(user.id, user.is_active)}
                              className="me-1"
                            >
                              {user.is_active ? 'تعطيل' : 'تفعيل'}
                            </Button>
                          )}
                          <Link to={`/dashboard/users/edit/${user.id}`}>
                            <Button size="sm" color="info" className="me-1">تعديل</Button>
                          </Link>
                          {user.id !== 1 && (
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => handleDelete(user.id)}
                            >
                              حذف
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(UsersList);
